import React from 'react';
import Header from '../../lib/AppBar';
import Footer from '../../lib/Footer';

import en from '../../lang/en-us';
import {
    Button,
    Dialog,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { useEffect } from 'react';
import { useState } from 'react';
import { OTP_ACTIONS } from '../../utils/constants';
import { saveSecretQuestions } from '../../redux-store/actions/login';
import { EncodeBase64 } from '../../utils/utilityFunctions';

const { secretQuestionnaire, setPassword } = en;

export interface GET_SECRET_QUESTION {
    reqId: string;
    otpRef: string;
    password: string;
    displayName: string;
}

export interface QUESTIONS_STRUCTURE {
    questionTypeId: string;
    secretQuestion: string;
    createdDateTime: number;
    show: boolean;
}

export interface secretQuestionsPayload {
    questionTypeId: string;
    secretAnswer: string;
}

export type selectedQuestionValidation = {
    question_id: number;
    index: number;
};

export interface questionnaireProps {
    questions: QUESTIONS_STRUCTURE[];
    _handleSelection: Function;
    value: string;
    _handleTextChange: Function;
    error: string;
    answerError: string;
    index: number;
    variant: 'filled' | 'standard' | 'outlined' | undefined;
    answer: string;
    isUpdate: boolean;
}

export function QuestionnaireFields({
    questions = [],
    _handleSelection = (id: number) => {},
    value = '',
    _handleTextChange = (value: string) => {},
    error = '',
    answerError = '',
    index = 0,
    variant = 'filled',
    answer = '',
    isUpdate = false,
}: questionnaireProps) {
    const questionLabelId = `secret-question-${index}`;
    const answerInputId = `answer-${index}`;

    const questionVisibleLabel = `${secretQuestionnaire.selectQuestion}${index}`;
    const answerVisibleLabel = secretQuestionnaire.answer;

    return (
        <div className="signup-questionnaire-fields">
            <FormControl variant={variant} error={!!error}>
                <InputLabel id={questionLabelId}>{questionVisibleLabel}</InputLabel>
                <Select
                    labelId={questionLabelId}
                    SelectDisplayProps={{ 'aria-label': questionVisibleLabel }}
                    onChange={({ target: { value } }: any) => _handleSelection(value)}
                    value={value}
                >
                    {isUpdate
                        ? questions
                              .filter(
                                  (_ele: QUESTIONS_STRUCTURE) =>
                                      _ele.show || _ele.questionTypeId == value,
                              )
                              .map((_ele: QUESTIONS_STRUCTURE) => (
                                  <MenuItem value={_ele.questionTypeId}>
                                      {_ele.secretQuestion}
                                  </MenuItem>
                              ))
                        : questions.map((_ele: QUESTIONS_STRUCTURE) => (
                              <MenuItem value={_ele.questionTypeId}>{_ele.secretQuestion}</MenuItem>
                          ))}
                </Select>
                {!!error && <FormHelperText>{error}</FormHelperText>}
            </FormControl>
            <TextField
                id={answerInputId}
                label={answerVisibleLabel}
                variant={variant}
                value={answer}
                onChange={({ target: { value } }) => _handleTextChange(value)}
                error={!!answerError}
                helperText={answerError}
                inputProps={{
                    'aria-label': `${answerVisibleLabel} for ${questionVisibleLabel}`,
                }}
            />
        </div>
    );
}

function SignUpQuestionnaire(props: any) {
    const { reqId, data, pan, pekrn } = props.location.state || {};

    const [state, setState] = useState<any>([]);
    const [questionsData, setData] = useState<any>(data);
    const [selectedIds, setSelectedIds] = useState<selectedQuestionValidation[]>([]);
    const [errors, setErrors] = useState<any>(
        new Array(5).fill({
            question: '',
        }),
    );
    const [answerErrors, setAnswerErrors] = useState<any>([]);
    const [showDialog, setDialog] = useState<boolean>(false);

    useEffect(() => {
        if (!props.location.state) {
            props.history.push('/investor/signin');
        }
        setState(
            new Array(5).fill({
                questionTypeId: '',
                secretAnswer: '',
            }),
        );
    }, []);

    useEffect(() => {
        // Getting the ids of the currently selected questions
        let selectedQuestionIds: any = [];
        selectedIds.forEach((_ele: any) => {
            selectedQuestionIds.push(_ele.question_id);
        });

        let qData = data.map((_ele: any) => {
            //set show to false if current question id is selected else true.
            _ele.show = selectedQuestionIds.includes(_ele.questionTypeId) ? false : true;
            return _ele;
        });

        setData(qData);
    }, [selectedIds]);

    const handleQuestionSelection = (id: number, index: number) => {
        const findIndex = selectedIds.findIndex(
            (_ele: selectedQuestionValidation) => _ele.index === index,
        );
        findIndex === -1
            ? setSelectedIds([...selectedIds, { question_id: id, index }])
            : setSelectedIds(
                  selectedIds.map((_ele: selectedQuestionValidation) => {
                      if (index === _ele.index) {
                          return { question_id: id, index };
                      }
                      return _ele;
                  }),
              );
        const updatedIds = state.map((_ele: secretQuestionsPayload, ind: number) => {
            if (index === ind) {
                return {
                    ..._ele,
                    questionTypeId: id,
                };
            }
            return _ele;
        });
        setState(updatedIds);
    };

    const handleAnswerChange = (value: string, index: number) => {
        const updatedAnswers = state.map((_ele: secretQuestionsPayload, ind: number) => {
            if (index === ind) {
                return {
                    ..._ele,
                    secretAnswer: value,
                };
            }
            return _ele;
        });
        setState(updatedAnswers);
    };

    const validateForm = () => {
        let errors: any = [];
        let answerErrors: any = [];
        const checkForQuestionErrors = state
            .map((_ele: secretQuestionsPayload, index: number) => {
                if (!_ele.questionTypeId) {
                    answerErrors[index] = { question: 'Please select security question' };
                }
            })
            .filter((_ele: any) => _ele);
        const checkForErrors = state
            .map((_ele: secretQuestionsPayload, index: number) => {
                if (!_ele.secretAnswer) {
                    errors[index] = { answer: 'Please enter your answer' };
                }
                if (_ele.secretAnswer.trim().length > 100) {
                    errors[index] = { answer: 'Answer must be less than 100 characters' };
                }
            })
            .filter((_ele: any) => _ele);
        if (checkForErrors.length === 0 || checkForQuestionErrors.length === 0) {
            return true;
        } else {
            if (checkForErrors.length) {
                setAnswerErrors(errors);
            }
            if (checkForQuestionErrors.length) {
                setErrors(answerErrors);
            }
            return false;
        }
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            try {
                const payload = {
                    pan,
                    reqId,
                    action: OTP_ACTIONS.register,
                    data: state.map((question: any) => ({
                        ...question,
                        secretAnswer: EncodeBase64(question.secretAnswer),
                    })),
                    pekrn,
                };
                await props.saveSecretQuestions(payload);
                setDialog(true);
                setTimeout(() => props.history.push('/'), 3 * 1000);
            } catch (e: any) {
                alert(e.message);
            }
        }
    };

    return (
        <>
            <Header {...props} />
            <div className="container-fluid">
                <div className="login-content-outer">
                    <div className="maxWidth login-container">
                        <div className="login-content signup-questionnaire">
                            <div className="signup-questionnaire-inner">
                                <h3>{secretQuestionnaire.heading}</h3>
                                <p>
                                    {secretQuestionnaire.userId}
                                    {(pan || pekrn).toUpperCase()}
                                </p>
                                <div className="signup-questionnaire-form">
                                    {state.map((_ele: secretQuestionsPayload, index: number) => (
                                        <QuestionnaireFields
                                            questions={questionsData}
                                            value={state[index].questionTypeId || ''}
                                            answer={state[index].secretAnswer || ''}
                                            _handleSelection={(id: number) => {
                                                setErrors(
                                                    errors.map((_ele: string, ind: number) => {
                                                        if (index === ind) {
                                                            return { question: '' };
                                                        }
                                                        return _ele;
                                                    }),
                                                );
                                                handleQuestionSelection(id, index);
                                            }}
                                            _handleTextChange={(value: string) => {
                                                setAnswerErrors(
                                                    answerErrors.map(
                                                        (_ele: string, ind: number) => {
                                                            if (index === ind) {
                                                                return { answer: '' };
                                                            }
                                                            return _ele;
                                                        },
                                                    ),
                                                );
                                                handleAnswerChange(value, index);
                                            }}
                                            index={index + 1}
                                            error={errors[index]?.question || ''}
                                            answerError={answerErrors[index]?.answer || ''}
                                            variant={'filled'}
                                            isUpdate={true}
                                        />
                                    ))}
                                    <Button
                                        className="customBtn"
                                        variant="contained"
                                        disabled={
                                            errors.some((_el: any) => !!_el.question) ||
                                            answerErrors.some((_el: any) => !!_el.answer) ||
                                            !state.every(
                                                (_ele: secretQuestionsPayload) =>
                                                    _ele.questionTypeId && _ele.secretAnswer,
                                            )
                                        }
                                        onClick={handleSubmit}
                                    >
                                        {secretQuestionnaire.submit}
                                    </Button>
                                </div>
                                <Dialog open={showDialog} aria-labelledby="signup-success-modal">
                                    <div className="signup-success-modal">
                                        <img
                                            className="signup-success-character"
                                            src="/images/signup-success-character.png"
                                            alt=""
                                        />
                                        <div className="signup-success-modal-top">
                                            <h4 className="text-center">
                                                {setPassword.modal.heading}
                                            </h4>
                                        </div>
                                        <div className="signup-success-modal-bottom">
                                            <p className="text-center">{setPassword.modal.info}</p>
                                        </div>
                                    </div>
                                </Dialog>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
}

export default connect(null, { saveSecretQuestions })(SignUpQuestionnaire);
